"use client";
/* eslint-disable @next/next/no-img-element */
import React from "react";

const DEFAULT_FALLBACK = "/assets/images/dummy.jpg";

const RESPONSIVE_WIDTHS = [320, 480, 640, 768, 1024, 1366, 1600];
const DPR = [1, 2];
const GUMLET_PARAMS = "auto=format,compress";

/** Custom Props */
type GumletImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet"
> & {
  imageUrl?: string | null;
  fallbackSrc?: string;
  width?: number | string;
  height?: number | string;
  fill?: boolean;
  priority?: boolean;
};

/** Ensure URL is absolute or use fallback */
const ensureAbsolute = (src?: string | null) => {
  if (!src || typeof src !== "string") return DEFAULT_FALLBACK;
  if (src.startsWith("http")) return src; // full URL (your case)
  if (src.startsWith("/")) return src; // public folder asset
  return src; // keep as is if backend sends correct URL
};

const cleanGumletUrl = (url: string) => {
  try {
    const obj = new URL(url);

    obj.searchParams.delete("w");
    obj.searchParams.delete("h");
    obj.searchParams.delete("dpr");
    obj.searchParams.delete("auto");

    const search = obj.searchParams.toString();
    return search ? `${obj.origin}${obj.pathname}?${search}` : `${obj.origin}${obj.pathname}`;
  } catch {
    return url.split("?")[0];
  }
};

/** Build srcset for fill mode */
const buildResponsiveSrcSet = (cleanUrl: string) =>
  RESPONSIVE_WIDTHS.flatMap((w) =>
    DPR.map((dpr) => {
      const url = `${cleanUrl}${cleanUrl.includes("?") ? "&" : "?"}w=${w}&dpr=${dpr}&${GUMLET_PARAMS}`;
      return `${url} ${w * dpr}w`;
    })
  ).join(", ");

/** Build srcset for fixed size mode */
const buildFixedSrcSet = (cleanUrl: string, width: number, height: number) =>
  DPR.map((dpr) => {
    // const url = `${cleanUrl}${cleanUrl.includes("?") ? "&" : "?"}w=${width * dpr}&h=${height * dpr}&dpr=${dpr}&${GUMLET_PARAMS}`;
    // Gumlet automatically multiplies 'w' by 'dpr' internally. 
    // We strictly pass the logical width and let the CDN do the math.
    const url = `${cleanUrl}${cleanUrl.includes("?") ? "&" : "?"}w=${width}&h=${height}&dpr=${dpr}&${GUMLET_PARAMS}`;
    return `${url} ${dpr}x`;
  }).join(", ");

const GumletImage = React.forwardRef<HTMLImageElement, GumletImageProps>(
  (
    {
      imageUrl,
      fallbackSrc = DEFAULT_FALLBACK,
      fill,
      width,
      height,
      sizes = "100vw",
      priority = false,
      alt = "",
      onError,
      className,
      ...rest
    },
    ref
  ) => {
    const rawUrl = ensureAbsolute(imageUrl);
    const fallbackUrl = ensureAbsolute(fallbackSrc);

    const [src, setSrc] = React.useState(rawUrl);

    /** Update src when imageUrl changes */
    React.useEffect(() => {
      setSrc(rawUrl);
    }, [rawUrl]);

    const cleanUrl = cleanGumletUrl(src);

    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (src !== fallbackUrl) {
        setSrc(fallbackUrl); // apply fallback once
      }
      onError?.(e);
    };
    // console.log("priority-gumlet", priority);

    /** --------- FILL MODE --------- */
    if (fill) {
      const srcSet = buildResponsiveSrcSet(cleanUrl);
      const defaultSrc = `${cleanUrl}${cleanUrl.includes("?") ? "&" : "?"}w=800&${GUMLET_PARAMS}`;

      return (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          <img
            ref={ref}
            src={defaultSrc}
            srcSet={srcSet}
            sizes={sizes}
            alt={alt}
            onError={handleError}
            className={`w-full h-full ${className || ""}`}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            {...rest}
          />
        </div>
      );
    }

    /** --------- FIXED MODE --------- */
    if (!width || !height) {
      throw new Error("GumletImage requires width and height unless using fill mode.");
    }

    const numW = typeof width === "number" ? width : parseInt(width);
    const numH = typeof height === "number" ? height : parseInt(height);

    const srcSet = buildFixedSrcSet(cleanUrl, numW, numH);
    const defaultSrc = `${cleanUrl}${cleanUrl.includes("?") ? "&" : "?"}w=${numW}&h=${numH}&dpr=1&${GUMLET_PARAMS}`;

    return (
      <img
        ref={ref}
        src={defaultSrc}
        srcSet={srcSet}
        sizes={sizes}
        width={numW}
        height={numH}
        alt={alt}
        onError={handleError}
        className={className}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        {...rest} // includes loading, fetchpriority, decoding, etc.
      />
    );
  }
);

GumletImage.displayName = "GumletImage";
export default GumletImage;
